import { CancelSigningResult } from "types/Files";

/**
 * What actually happened when signing was cancelled.
 *
 * The endpoint answers 200 in every one of these cases, including the one
 * where it removed nothing at all, so the outcome has to be read off the
 * counters rather than off the HTTP status.
 */
export type CancelSigningOutcome =
  /** Someone had already signed, so the document was withdrawn instead. */
  | { kind: "withdrawn" }
  /** Nothing was removed — the user has to retry. */
  | { kind: "failed" }
  /** Removed, but the Dokobit session was already deleted (30-day expiry). */
  | { kind: "session_gone" }
  /** Some requests were removed, some stayed behind. */
  | { kind: "partial"; removed: number; total: number }
  /** Everyone left had signed, so the document was finalized. */
  | { kind: "completed" }
  /** Plain success. */
  | { kind: "success" };

/**
 * Classify a cancel-signing response.
 *
 * `withdrawn` comes first: the document as a whole was withdrawn, which is the
 * outcome whatever the counters say (a request signed in the gap only stays
 * as a signature on a withdrawn document). It never comes with `completed`.
 *
 * `completed` outranks every failure branch: the backend only reports it after
 * it really did finalize the document, which happens when the last outstanding
 * signer signs while the request is in flight — the conditional delete then
 * drops nothing and counts the row as failed, but the document is finished and
 * telling the user that cancelling failed would be the opposite of the truth.
 *
 * `session_gone` outranks `partial` on purpose: when the Dokobit session is
 * gone, "send the document for signing again" is the only thing the user can
 * act on, and it stays true whether or not a request or two stayed behind.
 */
export function classifyCancelSigningResult(
  result: CancelSigningResult | null | undefined
): CancelSigningOutcome {
  const removed = result?.removed ?? 0;
  const failed = result?.failed ?? 0;

  if (result?.withdrawn) return { kind: "withdrawn" };
  if (result?.completed) return { kind: "completed" };
  if (removed === 0 && failed > 0) return { kind: "failed" };
  if (result?.sessionGone) return { kind: "session_gone" };
  if (failed > 0) return { kind: "partial", removed, total: removed + failed };
  return { kind: "success" };
}
